<template>
  <el-dialog
    v-model="visible"
    :show-close="false"
    width="600px"
    top="15vh"
    class="search-dialog"
    @opened="handleOpened"
    @close="handleClose"
  >
    <div class="search-container">
      <div class="search-input-wrap">
        <el-icon class="search-icon"><Search /></el-icon>
        <input
          ref="searchInputRef"
          v-model="keyword"
          class="search-input"
          type="text"
          placeholder="搜索文档..."
          @input="handleSearch"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
          @keydown.enter.prevent="selectCurrent"
          @keydown.esc.prevent="handleClose"
        />
        <kbd class="shortcut-hint">ESC</kbd>
      </div>

      <div class="search-results" v-if="searchResults.length > 0">
        <div
          v-for="(item, index) in searchResults"
          :key="item.docId"
          class="result-item"
          :class="{ active: activeIndex === index }"
          @click="handleSelect(item)"
          @mouseenter="activeIndex = index"
        >
          <el-icon class="doc-icon"><Document /></el-icon>
          <div class="result-info">
            <div class="result-title">{{ item.title }}</div>
            <div class="result-meta">
              <span>{{ item.wordCount || 0 }} 字</span>
              <span v-if="item.updateTime"> · {{ item.updateTime }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="search-empty" v-else-if="searched && keyword">
        <el-icon :size="32"><Search /></el-icon>
        <p>未找到相关文档</p>
      </div>

      <div class="search-hint" v-else-if="!keyword">
        <p>输入关键词搜索文档标题和内容</p>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { ref, nextTick } from 'vue'
import { searchDoc } from '@/api/doc'
import { useRouter } from 'vue-router'

export default {
  name: 'GlobalSearch',
  setup() {
    const visible = ref(false)
    const keyword = ref('')
    const searchResults = ref([])
    const searched = ref(false)
    const activeIndex = ref(0)
    const searchInputRef = ref(null)
    const router = useRouter()

    let searchTimer = null

    const open = () => {
      visible.value = true
      keyword.value = ''
      searchResults.value = []
      searched.value = false
      activeIndex.value = 0
    }

    const handleOpened = () => {
      nextTick(() => {
        searchInputRef.value?.focus()
      })
    }

    const handleClose = () => {
      visible.value = false
    }

    const handleSearch = () => {
      if (searchTimer) clearTimeout(searchTimer)
      if (!keyword.value.trim()) {
        searchResults.value = []
        searched.value = false
        return
      }
      searchTimer = setTimeout(async () => {
        try {
          const res = await searchDoc(keyword.value.trim())
          searchResults.value = res.data || []
          searched.value = true
          activeIndex.value = 0
        } catch {
          searchResults.value = []
          searched.value = true
        }
      }, 300)
    }

    const navigateDown = () => {
      if (activeIndex.value < searchResults.value.length - 1) {
        activeIndex.value++
      }
    }

    const navigateUp = () => {
      if (activeIndex.value > 0) {
        activeIndex.value--
      }
    }

    const selectCurrent = () => {
      if (searchResults.value[activeIndex.value]) {
        handleSelect(searchResults.value[activeIndex.value])
      }
    }

    const handleSelect = (item) => {
      handleClose()
      const routeData = router.resolve(`/book/${item.bookId}?docId=${item.docId}`)
      window.open(routeData.href, '_blank')
    }

    return {
      visible,
      keyword,
      searchResults,
      searched,
      activeIndex,
      searchInputRef,
      open,
      handleOpened,
      handleClose,
      handleSearch,
      navigateDown,
      navigateUp,
      selectCurrent,
      handleSelect,
    }
  },
}
</script>

<style scoped>
:deep(.search-dialog .el-dialog__header) {
  display: none;
  padding: 0;
}
:deep(.search-dialog .el-dialog__body) {
  padding: 0;
}
:deep(.search-dialog) {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
}

.search-container {
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.search-input-wrap {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
}
.search-icon {
  font-size: 20px;
  color: #8c8c8c;
  flex-shrink: 0;
}
.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #262626;
  background: transparent;
}
.search-input::placeholder {
  color: #bfbfbf;
}
.shortcut-hint {
  font-size: 11px;
  color: #8c8c8c;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 2px 6px;
  font-family: monospace;
}

.search-results {
  overflow-y: auto;
  max-height: 400px;
  padding: 8px;
}
.result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}
.result-item:hover,
.result-item.active {
  background: #f0f5ff;
}
.doc-icon {
  font-size: 18px;
  color: #8c8c8c;
  flex-shrink: 0;
}
.result-info {
  flex: 1;
  min-width: 0;
}
.result-title {
  font-size: 14px;
  color: #262626;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-meta {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.search-empty,
.search-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: #bfbfbf;
}
.search-empty p,
.search-hint p {
  margin-top: 12px;
  font-size: 14px;
}
</style>
